import { Data } from "@core/content/types";
import {
  createCreatorContentStore,
  toTargetNames as toTargetNamesCore,
} from "@core/content/creator/CreatorContentStore";
import type { CreatorPayload } from "@core/content/creator/types";

export const toTargetNames = toTargetNamesCore;

// Local icon overrides used for both the carousel thumbnails and the
// CoachingOverlay. Filenames are `Artwork-<id>.png` so we match by id.
const contentIconModules = import.meta.glob<string>(
  "../assets/images/content-icons/Artwork-*.webp",
  { eager: true, query: "?url", import: "default" }
);

const contentIconsById: Record<string, string> = Object.fromEntries(
  Object.entries(contentIconModules)
    .map(([path, url]) => {
      const match = path.match(/(Artwork-[a-f0-9-]+)\.webp$/);
      return match ? [match[1], url] : null;
    })
    .filter((entry): entry is [string, string] => entry !== null)
);

const applyLocalIcons = (items: Data[]): Data[] =>
  items.map((item) => {
    const localIcon = contentIconsById[item.id];
    return localIcon ? { ...item, icon: localIcon } : item;
  });

const applyArtworkOrder = (items: Data[], order: string[]): Data[] => {
  if (!order.length) return items;
  const rank = new Map(order.map((id, index) => [id, index]));
  const fallback = order.length;
  return [...items].sort((a, b) => {
    const ra = rank.get(a.id) ?? fallback;
    const rb = rank.get(b.id) ?? fallback;
    return ra - rb;
  });
};

// Fully decode every content icon before resolving, so the carousel and
// CoachingOverlay always paint a real bitmap on first render instead of a
// still-loading bg-image div. Awaited from initializeCreatorData so the
// splash screen holds until icons are cached.
let contentIconsPreloadPromise: Promise<void> | null = null;

export const preloadContentIcons = (): Promise<void> => {
  if (typeof window === "undefined") return Promise.resolve();
  if (contentIconsPreloadPromise) return contentIconsPreloadPromise;
  contentIconsPreloadPromise = Promise.all(
    Object.values(contentIconsById).map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          const done = () => resolve();
          img.onload = done;
          img.onerror = done;
          img.src = url;
          if (img.decode) {
            img.decode().then(done, done);
          }
        }),
    ),
  ).then(() => undefined);
  return contentIconsPreloadPromise;
};

/** Pre-loaded artwork data injected by SSR (launch-v4). */
const injectedPayload: CreatorPayload | undefined =
  typeof window !== "undefined"
    ? (window as any).__ARTWORK_DATA__
    : undefined;

let manualContent: Data[] = [];
let creatorDataSource = "";
let defaultMicrositeData = { name: "", description: "", constructedCover: "" };
let artworkOrder: string[] = [];

export let ContentData: Data[] = [...manualContent];
export let MicrositeData = { ...defaultMicrositeData };
export let INITIAL_CONTENT_ID = "";
export let CreatorPayloadData: CreatorPayload | null = injectedPayload ?? null;

let creatorContentStore = createCreatorContentStore({ creatorDataSource: "", manualContent: [] });

export const configureCreatorContentStore = (options: {
  creatorDataSource: string;
  manualContent: Data[];
  defaultMicrositeData?: { name: string; description: string; constructedCover: string };
  artworkOrder?: string[];
}): void => {
  manualContent = options.manualContent;
  creatorDataSource = options.creatorDataSource;
  defaultMicrositeData = options.defaultMicrositeData ?? { name: "", description: "", constructedCover: "" };
  artworkOrder = options.artworkOrder ?? [];
  ContentData = [...manualContent];
  MicrositeData = { ...defaultMicrositeData };
  INITIAL_CONTENT_ID = "";
  CreatorPayloadData = injectedPayload ?? null;
  creatorContentStore = createCreatorContentStore({
    creatorDataSource: options.creatorDataSource,
    manualContent,
    injectedPayload,
  });
};

export const initializeCreatorData = async (): Promise<void> => {
  // No collection linked — keep manual content + default microsite copy.
  if (!creatorDataSource && !injectedPayload) {
    ContentData = [...manualContent];
    MicrositeData = { ...defaultMicrositeData };
    INITIAL_CONTENT_ID = "";
    CreatorPayloadData = null;
    return;
  }
  const state = await creatorContentStore.initialize();
  ContentData = applyArtworkOrder(applyLocalIcons(state.contentData), artworkOrder);
  MicrositeData = state.micrositeData;
  INITIAL_CONTENT_ID = ContentData[0]?.id ?? state.initialContentId;
  CreatorPayloadData = state.creatorPayload;
  // await preloadContentIcons();
};

export const getContent = (id: string) => {
  return creatorContentStore.getContent(id);
};

export const getCreatorPayload = (): CreatorPayload | null => {
  return CreatorPayloadData;
};
